import React, { useEffect, useState } from 'react';
import { getAllUsers, createUser, updateUserRole, deleteUser } from '../../api';
import { getWmsAlmacenes, assignWarehousesToUser, getWarehousesByUser, getWmsUsuariosWithWarehouses } from '../../api.wms';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Users, Edit, Trash } from 'lucide-react';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import WarehouseIcon from '@mui/icons-material/Warehouse';

// Define los roles por sistema
export const OMS_ROLES = [
  'admin', 'user', 'client', 'Procurement', 'Trafico', 'Documental', 'Sourcing', 'Multicuentas', 'Legal', 'ControlTower', 'Propios'
];
export const WMS_ROLES = [
  'admin', 'user','Creado', 'wms_almacenista', 'wms_supervisor', 'wms_operador', 'wms_cliente', 'wms_inventarios', 'wms_auditor', 'wms_logistica'
];

const UserForm = ({ open, onClose, onSave, user, roles }) => {
  const [form, setForm] = useState(user || { name: '', email: '', role: roles[0] || 'user', password: '', almacenes: [] });
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    setForm(user || { name: '', email: '', role: roles[0] || 'user', password: '', almacenes: [] });
  }, [user, roles]);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const data = await getWmsAlmacenes();
        setWarehouses(data);
      } catch (e) {
        setWarehouses([]);
      }
    };
    if (open) fetchWarehouses();
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{user ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 350 }}>
        <TextField label="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        <TextField label="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        <Select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} label="Rol">
          {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
        </Select>
        <FormControl>
          <InputLabel id="almacenes-label">Almacenes</InputLabel>
          <Select
            labelId="almacenes-label"
            multiple
            value={form.almacenes || []}
            onChange={e => setForm({ ...form, almacenes: e.target.value })}
            renderValue={selected =>
              warehouses.filter(w => selected.includes(w.id)).map(w => w.nombre).join(', ')
            }
          >
            {warehouses.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.nombre}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {!user && <TextField label="Contraseña" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={() => onSave(form)} variant="contained">Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const AssignWarehousesModal = ({ open, onClose, user, allWarehouses, onSave }) => {
  const [selected, setSelected] = useState([]);
  useEffect(() => {
    if (user && open) {
      getWarehousesByUser(user.id).then(ids => setSelected(ids)).catch(() => setSelected([]));
    }
  }, [user, open]);
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Asignar almacenes a {user?.name}</DialogTitle>
      <DialogContent sx={{ minWidth: 350 }}>
        <FormControl fullWidth>
          <InputLabel id="asignar-almacenes-label">Almacenes</InputLabel>
          <Select
            labelId="asignar-almacenes-label"
            multiple
            value={selected}
            onChange={e => setSelected(e.target.value)}
            renderValue={sel => allWarehouses.filter(w => sel.includes(w.id)).map(w => w.nombre).join(', ')}
          >
            {allWarehouses.map(w => (
              <MenuItem key={w.id} value={w.id}><WarehouseIcon fontSize="small" sx={{mr:1}} />{w.nombre}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={() => { onSave(selected); onClose(); }} variant="contained">Guardar</Button>
      </DialogActions>
    </Dialog>
  );
};

const WmsUsuarios = ({ roles = WMS_ROLES }) => {
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [userToAssign, setUserToAssign] = useState(null);

  // Cambia aquí para usar el endpoint WMS
  const fetchUsers = async () => {
    const data = await getWmsUsuariosWithWarehouses();
    setUsers(data.filter(u => roles.includes(u.role)));
  };
  useEffect(() => { fetchUsers(); }, [roles]);

  useEffect(() => { getWmsAlmacenes().then(setWarehouses); }, []);

  const handleSave = async (form) => {
    if (editUser) {
      // Solo permitir cambiar el rol del usuario
      if (form.role !== editUser.role) {
        await updateUserRole(editUser.id, form.role);
      }
      // Si se quisiera editar nombre/email, aquí se agregaría lógica adicional
    } else {
      await createUser(form);
    }
    setModalOpen(false); setEditUser(null); fetchUsers();
  };
  const handleDelete = async (id) => {
    if (window.confirm('¿Eliminar usuario?')) {
      await deleteUser(id); fetchUsers();
    }
  };

  // Agrupa los usuarios por rol
  const groupedUsers = users.reduce((acc, user) => {
    if (!acc[user.role]) acc[user.role] = [];
    acc[user.role].push(user);
    return acc;
  }, {});

  return (
    <>
      <Button variant="contained" sx={{ mb: 2 }} onClick={() => { setEditUser(null); setModalOpen(true); }}>Nuevo usuario</Button>
      <table className="min-w-full bg-white rounded shadow mb-4">
        <thead>
          <tr>
            <th className="px-4 py-2">Nombre</th>
            <th className="px-4 py-2">Email</th>
            <th className="px-4 py-2">Rol</th>
            <th className="px-4 py-2">Almacenes</th>
            <th className="px-4 py-2">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(groupedUsers).map(([role, usersInRole]) => (
            <React.Fragment key={role}>
              <tr className="bg-gray-100">
                <td colSpan={5} className="font-bold px-4 py-2 text-blue-700 uppercase">{role}</td>
              </tr>
              {usersInRole.map(u => (
                <tr key={u.id}>
                  <td className="px-4 py-2">{u.name}</td>
                  <td className="px-4 py-2">{u.email}</td>
                  <td className="px-4 py-2">{u.role}</td>
                  <td className="px-4 py-2">{Array.isArray(u.almacenes) ? u.almacenes.map(a => a.nombre || a).join(', ') : ''}</td>
                  <td className="px-4 py-2 flex gap-1">
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Asignar almacenes"
                      onClick={() => { setUserToAssign(u); setAssignModalOpen(true); }}
                    >
                      <WarehouseIcon fontSize="small" />
                    </button>
                    <button
                      className="p-1 hover:bg-gray-100 rounded mr-1"
                      title="Editar"
                      onClick={() => { setEditUser(u); setModalOpen(true); }}
                    >
                      <Edit size={18} className="text-blue-600" />
                    </button>
                    <button
                      className="p-1 hover:bg-gray-100 rounded"
                      title="Eliminar"
                      onClick={() => handleDelete(u.id)}
                    >
                      <Trash size={18} className="text-red-600" />
                    </button>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <UserForm open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} user={editUser} roles={roles} />
      <AssignWarehousesModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        user={userToAssign}
        allWarehouses={warehouses}
        onSave={async (selected) => {
          if (userToAssign) await assignWarehousesToUser(userToAssign.id, selected);
          fetchUsers();
        }}
      />
    </>
  );
};

export default WmsUsuarios;
